import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";
import {
  formatFractionLatex,
  formatMixedFractionLatex,
} from "../../../../utils/fractionLatex";

// Pomocnicze funkcje do pracy na ułamkach

function randomInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}


function fracMul(f1, f2) {
  return { num: f1.num * f2.num, den: f1.den * f2.den };
}

function fracPow(frac, k) {
  return { num: Math.pow(frac.num, k), den: Math.pow(frac.den, k) };
}

function fracToNumber(f) {
  return f.num / f.den;
}

function gcd(a, b) {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b !== 0) {
    const t = b;
    b = a % b;
    a = t;
  }
  return a;
}

function simplify(frac) {
  const d = gcd(frac.num, frac.den);
  return { num: frac.num / d, den: frac.den / d };
}

function isIntegerFrac(frac) {
  // po uproszczeniu, jeśli mianownik = 1 → liczba całkowita
  return frac.den === 1;
}

// minus wyciągamy przed ułamek: -\frac{3}{2}
const fracToLatex = (fraction) =>
  formatFractionLatex(fraction.num, fraction.den);

// ułamek mieszany: -1\frac{1}{2}, jeśli |num| >= den
const fracToMixedLatex = (fraction) =>
  formatMixedFractionLatex(fraction.num, fraction.den);

// ==== GENERATOR ====
//
// Zadanie: oblicz a_n w ciągu geometrycznym z ułamkowymi a1, q.

function makeFraction() {
  const denOptions = [2, 3, 4];
  const den = denOptions[Math.floor(Math.random() * denOptions.length)];
  let num;
  do {
    num = randomInt(-4, 4); // było -9..9, teraz tniemy do -4..4
  } while (num === 0);
  return { num, den };
}

export function generateTask() {
  let a1Frac, qFrac, a1S, qS;

  // losujemy tak długo, aż NIE będą jednocześnie całkowite po skróceniu
  do {
    a1Frac = makeFraction();
    qFrac = makeFraction();
    a1S = simplify(a1Frac);
    qS = simplify(qFrac);
  } while (isIntegerFrac(a1S) && isIntegerFrac(qS));

  // ====== reguły potęgowania zależnie od "większej" liczby w ułamku ======
  // baza = max(|licznik|, mianownik) (bardziej rygorystyczny warunek)
  const base = Math.max(Math.abs(qS.num), Math.abs(qS.den));

  // min wykładnik: 2 (czyli n >= 3)
  const minExp = 2;

  // max wykładnik wg Twoich zasad
  let maxExp;
  if (base === 2) maxExp = 10;      // q ~ 2 lub 1/2 itp.
  else if (base === 3) maxExp = 5;  // q ~ 3 lub 1/3 itd.
  else if (base === 4) maxExp = 4;  // q ~ 4 lub 3/4 itd.
  else if (base === 5) maxExp = 4;
  else maxExp = 3;                  // reszta

  // ====== losowanie po "przypadkach" (k) z wagami dla mniejszych baz ======
  // Ułamki i tak mamy tylko z bazą 2..4, ale zostawiamy uniwersalnie.
  const weightByBase = {
    2: 3,
    3: 2,
    4: 1,
    5: 1
  };
  const w = weightByBase[base] ?? 1;

  // losujemy wykładnik k w [minExp..maxExp] z równą szansą dla każdego k,
  // a preferencję dla mniejszych baz realizujemy wagą na poziomie "case"
  const exps = [];
  for (let k = minExp; k <= maxExp; k++) exps.push({ k, w });

  const pickedK = weightedPick(exps).k;
  const n = pickedK + 1;

  const qPow = simplify(fracPow(qS, pickedK));
  const a_nFrac = simplify(fracMul(a1S, qPow));

  const a1Value = fracToNumber(a1S);
  const qValue = fracToNumber(qS);
  const a_nValue = fracToNumber(a_nFrac);

  return {
    type: "FindNthTermGeoFracTask",
    data: {
      n,
      a1: a1Value,
      q: qValue,
      a_n: a_nValue,
      a1Frac: a1S,
      qFrac: qS,
      a_nFrac
    }
  };
}

// helper (może być wklejony na dole pliku, jeśli go nie masz globalnie)
function weightedPick(items) {
  const total = items.reduce((s, it) => s + it.w, 0);
  let r = Math.random() * total;
  for (const it of items) {
    r -= it.w;
    if (r <= 0) return it;
  }
  return items[items.length - 1];
}


// ==== RENDER ====

export function renderTask({ data }) {
  const { a1Frac, qFrac, n } = data;

  const a1Latex = fracToLatex(a1Frac);
  const qLatex = fracToLatex(qFrac);

  return (
    <>
      <p>
        Dany jest ciąg geometryczny, w którym{" "}
        <InlineMath math={`a_1 = ${a1Latex}`} /> oraz{" "}
        <InlineMath math={`q = ${qLatex}`} />.
      </p>
      <p>
        Oblicz wyraz <InlineMath math={`a_{${n}}`} /> tego ciągu.
      </p>
    </>
  );
}

export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <div className="flex items-center gap-2">
      <InlineMath math="a_n =" />
      <FractionInput
        value={userAnswer}
        onChange={onChange}
        disabled={disabled}
        label="a_n"
      />
    </div>
  );
}

// Akceptujemy:
// - ułamek mieszany (parser robi z niego liczbę),
// - ułamek niewłaściwy,
// - postać dziesiętną (dokładną lub zaokrągloną).
export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object") return false;

  const given = userAnswer.parsed;
  if (given === null || isNaN(given)) return false;

  // tolerancja pozwala na zaokrąglenia (np. do 0,1)
  return Math.abs(given - data.a_n) < 0.01;
}

export function renderAnswer({ data }) {
  const { n, a_nFrac } = data;
  const mixedLatex = fracToMixedLatex(a_nFrac);

  return <InlineMath math={`a_{${n}} = ${mixedLatex}`} />;
}

export function renderExplanation({ data }) {
  const { a1Frac, qFrac, n, a_nFrac } = data;

  const a1Latex = fracToLatex(a1Frac);
  const qLatex = fracToLatex(qFrac);
  const qPow = simplify(fracPow(qFrac, n - 1));
  const qPowLatex = fracToLatex(qPow);
  const a_nLatex = fracToLatex(a_nFrac);
  const a_nMixedLatex = fracToMixedLatex(a_nFrac);

  const exponent = n - 1;

  return (
    <div className="space-y-2">
      <p>
        <strong>Krok 1:</strong> Korzystamy ze wzoru ogólnego ciągu
        geometrycznego:
      </p>
      <BlockMath math={`a_n = a_1 \\cdot q^{n-1}`} />

      <p>
        <strong>Krok 2:</strong> Podstawiamy dane:
      </p>
      <BlockMath
        math={`a_{${n}} = ${a1Latex} \\cdot \\left(${qLatex}\\right)^{${n}-1}`}
      />
      <BlockMath
        math={`a_{${n}} = ${a1Latex} \\cdot \\left(${qLatex}\\right)^{${exponent}}`}
      />
      <BlockMath math={`a_{${n}} = ${a1Latex} \\cdot ${qPowLatex}`} />

      <p>
        <strong>Krok 3:</strong> Mnożymy ułamki i zapisujemy wynik:
      </p>
      <BlockMath
        math={`a_{${n}} = ${a_nLatex} = ${a_nMixedLatex}`}
      />
    </div>
  );
}

export function renderUserAnswer(userAnswer, task) {
  const n = task?.data?.n;

  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) {
    return <i>(brak odpowiedzi)</i>;
  }

  return <InlineMath math={`a_{${n}} = ${userAnswer.raw}`} />;
}
